import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { tokenStorage } from '@services/tokenStorage'
import { getRealtimeConnectionId, CONNECTION_ID_HEADER } from '@services/realtimeIdentity'
import { NDA_REQUIRED_CODE } from './types/nda'

const API_BASE_URL = 'https://api.hadv.quest'

// Function to handle logout - will be set by the app on initialization
let logoutHandler: (() => void) | null = null

export function setLogoutHandler(handler: () => void) {
  logoutHandler = handler
}

function performLogout() {
  tokenStorage.clearTokens()
  if (logoutHandler) {
    logoutHandler()
  }
}

// Function to handle the server's NDA gate - will be set by the app on initialization,
// the same way the logout handler is, so this module stays free of store imports.
let ndaRequiredHandler: ((version: number | null) => void) | null = null

export function setNdaRequiredHandler(handler: (version: number | null) => void) {
  ndaRequiredHandler = handler
}

// Error bodies are normally parsed JSON, but the backend has been known to send the wrong
// Content-Type, in which case axios leaves a string behind. Read either.
function readErrorBody(data: unknown): { error?: string; version?: number | null } | null {
  if (!data) return null
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch {
      return null
    }
  }
  if (typeof data === 'object') return data as { error?: string; version?: number | null }
  return null
}

/**
 * Query strings the API can actually read.
 *
 * Axios writes an array as `categoryIds[]=9&categoryIds[]=7`. ASP.NET Core binds `List<int>` from
 * a repeated key with no brackets, so the bracketed form does not bind — and, because an absent
 * filter means "no filter", the request silently returned everything instead of failing. That is
 * what made the category filter on My Adventures look like it did nothing: the server was fine,
 * the parameter never reached it. (Measured: `categoryIds=9` gives 21,865 rows, `categoryIds[]=9`
 * gives all 37,844.)
 *
 * Repeating the bare key is what the API expects, so every array query parameter in the app binds
 * from here on. Nothing depended on the old form — under it, no array filter bound at all.
 */
function serializeParams(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  const append = (key: string, value: unknown) => {
    if (value === undefined || value === null) return
    search.append(key, value instanceof Date ? value.toISOString() : String(value))
  }
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach((item) => append(key, item))
    else append(key, value)
  })
  return search.toString()
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  paramsSerializer: { serialize: serializeParams },
})

// Flag to prevent multiple refresh attempts
let isRefreshing = false
let failedRequestsQueue: Array<(token: string) => void> = []
// One shared refresh for requests that all came back 401 together
let retryRefresh: Promise<string> | null = null

// Rate-limit backoff, per endpoint. When a response comes back 429, further requests to that
// same endpoint are held off until this timestamp passes (server-driven via the Retry-After
// header). Prevents poll loops from hammering an already throttled endpoint and tripping the
// limit on a fresh bucket cycle.
//
// Per endpoint, not global: the server limits by bucket (the location heartbeat has a bucket of
// its own), so one refused heartbeat used to fail every search and page load in the tab for up
// to a minute with "Rate limit backoff", none of which the server would have refused.
const rateLimitPauseUntil = new Map<string, number>()

/** Method and path, without the query string: what a pause applies to. */
export function rateLimitKey(config: { method?: string; url?: string } | undefined): string {
  const path = (config?.url ?? '').split('?')[0].replace(/^https?:\/\/[^/]+/, '')
  return `${(config?.method ?? 'get').toUpperCase()} ${path}`
}

function setRateLimitPause(key: string, retryAfterSeconds: number) {
  // Cap at 60s — the server's policy windows are short and we don't want a
  // mis-set Retry-After to freeze an endpoint indefinitely.
  const clamped = Math.min(Math.max(retryAfterSeconds, 1), 60)
  const next = Date.now() + clamped * 1000
  if (next > (rateLimitPauseUntil.get(key) ?? 0)) {
    rateLimitPauseUntil.set(key, next)
  }
}

function rateLimitPauseRemaining(key: string): number {
  const until = rateLimitPauseUntil.get(key) ?? 0
  const left = until - Date.now()
  if (left <= 0 && until) rateLimitPauseUntil.delete(key)
  return Math.max(left, 0)
}

// Request interceptor - attach JWT token and handle auto-refresh
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Rate-limit gate: if a previous 429 from this endpoint told us to back
    // off, fail this request fast with a synthetic 429 instead of sending it.
    // Polling queries will see the rejection, count it as a failed fetch, and
    // try again on their next refetchInterval — at which point the pause may
    // have cleared. Mutations bubble the error up to the caller's onError.
    const pausedMs = rateLimitPauseRemaining(rateLimitKey(config))
    if (pausedMs > 0) {
      const retryAfter = Math.ceil(pausedMs / 1000)
      const err: AxiosError = Object.assign(new Error('Rate limited (client-side backoff)') as any, {
        isAxiosError: true,
        config,
        response: {
          status: 429,
          statusText: 'Too Many Requests',
          headers: { 'retry-after': String(retryAfter) },
          data: { error: 'Rate limit backoff', retryAfter },
          config,
        } as AxiosResponse,
      })
      return Promise.reject(err)
    }

    // Skip token refresh for auth endpoints
    const isAuthEndpoint = config.url?.includes('/api/auth/')

    if (!isAuthEndpoint) {
      // Check if access token is expired or expiring soon
      if (tokenStorage.isAccessTokenExpired() && !tokenStorage.isRefreshTokenExpired()) {
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve) => {
            failedRequestsQueue.push((token: string) => {
              if (config.headers) {
                config.headers.Authorization = `Bearer ${token}`
              }
              resolve(config)
            })
          })
        }

        isRefreshing = true

        try {
          const newToken = await refreshToken()
          if (newToken && config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`
          }

          // Process queued requests
          failedRequestsQueue.forEach(callback => callback(newToken))
          failedRequestsQueue = []
        } catch (error) {
          // Refresh failed, clear queue and logout
          failedRequestsQueue = []
          performLogout()
          throw error
        } finally {
          isRefreshing = false
        }
      } else {
        // Use current access token
        const token = tokenStorage.getAccessToken()
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
      }
    } else {
      // For auth endpoints, just attach current token if available
      const token = tokenStorage.getAccessToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // Name this tab's realtime connection, so a settings change made here can be told apart from
    // one made on another device. Harmless when the hub is not connected — the header is simply
    // absent and the server treats every device as a stranger, which only costs one extra refresh.
    const realtimeId = getRealtimeConnectionId()
    if (realtimeId && config.headers) {
      config.headers[CONNECTION_ID_HEADER] = realtimeId
    }

    // Log requests in development with full details
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`)
      console.log('[API Request] Full URL:', (config.baseURL || '') + (config.url || ''))
      console.log('[API Request] Headers:', config.headers)
      console.log('[API Request] Data:', config.data)
    }

    return config
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log responses in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.config.url}`, response.status, response.data)
    }
    return response
  },
  async (error: AxiosError) => {
    // Log errors in development with full details
    if (import.meta.env.DEV) {
      console.error('[API Error] URL:', error.config?.url)
      console.error('[API Error] Status:', error.response?.status)
      console.error('[API Error] Message:', error.message)
      console.error('[API Error] Response:', error.response?.data)
      console.error('[API Error] Full Error:', error)
    }

    // Handle 429 Too Many Requests - record the server-supplied Retry-After
    // and globally pause outgoing requests until it expires. Affects every
    // caller, so a single rate-limited endpoint doesn't fight against
    // unrelated polls also burning the bucket.
    if (error.response?.status === 429) {
      const header = error.response.headers?.['retry-after'] ?? error.response.headers?.['Retry-After']
      const retryAfter = typeof header === 'string' ? parseInt(header, 10) : NaN
      setRateLimitPause(rateLimitKey(error.config), Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 5)
    }

    // Handle 401 Unauthorized - logout and redirect
    if (error.response?.status === 401) {
      // An unexpired access token can still be refused (its session was revoked, or it was
      // issued before the server started binding tokens to sessions). Refresh once and retry;
      // the refresh itself decides whether the session is really over.
      const original = error.config as (InternalAxiosRequestConfig & { _retried401?: boolean }) | undefined
      if (
        original &&
        !original._retried401 &&
        !original.url?.includes('/api/auth/') &&
        tokenStorage.getRefreshToken() &&
        !tokenStorage.isRefreshTokenExpired()
      ) {
        original._retried401 = true
        try {
          retryRefresh ??= refreshToken().finally(() => { retryRefresh = null })
          const newToken = await retryRefresh
          original.headers.Authorization = `Bearer ${newToken}`
          return apiClient(original)
        } catch {
          performLogout()
          return Promise.reject(error)
        }
      }

      // If refresh token is expired, logout
      if (tokenStorage.isRefreshTokenExpired()) {
        performLogout()

        // Only redirect if not already on a public auth page
        const currentPath = window.location.pathname
        const publicPaths = [
          '/login',
          '/register',
          '/register/verify-phone',
          '/register/complete',
          '/forgot-password',
          '/verify-reset-code',
          '/reset-password'
        ]
        if (!publicPaths.some(path => currentPath.startsWith(path))) {
          window.location.href = '/login'
        }
      }
    }

    // Handle 403 nda_required - this account has not accepted the non-disclosure agreement in
    // force, and the server is refusing everything until it does. Distinct from a plain 403,
    // which means "you may not do that": this one means "you have not signed yet", and there is
    // a screen for it. Raising a flag (rather than redirecting) lets the gate that already wraps
    // every authenticated route swap itself in where the user stands, so nothing is navigated
    // away from and nothing is lost. Deliberately separate from the 401 branch above: that one
    // clears tokens and sends people to /login, which is the wrong answer here — the session is
    // perfectly valid, it is the agreement that is outstanding.
    if (error.response?.status === 403) {
      const body = readErrorBody(error.response.data)
      if (body?.error === NDA_REQUIRED_CODE) {
        const version = typeof body.version === 'number' ? body.version : null
        if (ndaRequiredHandler) {
          ndaRequiredHandler(version)
        }
      }
    }

    // Handle USER_NOT_FOUND error - account no longer exists
    const errorData = error.response?.data as { code?: string } | undefined
    if (errorData?.code === 'USER_NOT_FOUND') {
      performLogout()

      // Redirect to login if not already on a public auth page
      const currentPath = window.location.pathname
      const publicPaths = [
        '/login',
        '/register',
        '/register/verify-phone',
        '/register/complete',
        '/forgot-password',
        '/verify-reset-code',
        '/reset-password'
      ]
      if (!publicPaths.some(path => currentPath.startsWith(path))) {
        window.location.href = '/login'
      }
    }

    // Handle other errors
    handleApiError(error)
    return Promise.reject(error)
  }
)

// Token refresh function
async function refreshToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken()

  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  try {
    const response = await axios.post(
      'https://api.hadv.quest/api/auth/refresh',
      { refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
      }
    )

    const { accessToken, refreshToken: newRefreshToken, expiresAt, refreshTokenExpiresAt } = response.data

    // Store new tokens
    tokenStorage.setTokens({
      accessToken,
      refreshToken: newRefreshToken,
      expiresAt,
      refreshTokenExpiresAt,
    })

    return accessToken
  } catch (error) {
    console.error('[Token Refresh Failed]', error)
    tokenStorage.clearTokens()
    throw error
  }
}

// Global error handler
function handleApiError(error: AxiosError) {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status
    const message = (error.response.data as any)?.message || error.message

    console.error(`[API Error ${status}]`, message, error.response.data)

    // Show user-friendly error messages
    switch (status) {
      case 400:
        console.error('Bad Request:', message)
        break
      case 401:
        console.error('Unauthorized:', message)
        break
      case 403:
        console.error('Forbidden:', message)
        break
      case 404:
        console.error('Not Found:', message)
        break
      case 500:
        console.error('Server Error:', message)
        break
      default:
        console.error('API Error:', message)
    }
  } else if (error.request) {
    // Request made but no response
    console.error('[Network Error] No response received', error.request)
  } else {
    // Request setup error
    console.error('[Request Error]', error.message)
  }
}

export default apiClient
